import _ from 'lodash'
import $ from 'jquery'
import Backbone from 'backbone'
import template from './upload-spreadsheet.hbs'
import FormView from 'scoreboard/components/form-view'
import NewRecordOptions from 'scoreboard/datasets/new-record-options'
import i18n from 'scoreboard/components/i18n'
import { handleMissingColumnsException } from 'scoreboard/imports/new-column-name-chooser-wizard'
import FileDrop from 'scoreboard/components/files/file-drop'
import { filenameIsExcelFile } from 'scoreboard/components/spreadsheet-file-utils'

// Here in this view, the file drop needs to handle errors a little differently based on
// what kind of error it's presented with (#36919), so extend this custom file drop to handle that.
const UploadSpreadsheetFileDrop = FileDrop.extend({
	uploadFinished() {
		this.fileUploader.uploadFinished()
	},

	_handleFileError(xhr) {
		this.trigger('error', {
			xhr,
			fileDrop: this
		})
	}
})

const UploadSpreadsheet = Backbone.View.extend({

	tagName: 'form',

	className: 'datasets-upload-spreadsheet two-box-options-dialog',

	events: {
		'click .is-navigable[data-step=1]': 'cancelUpload',
		'click .back': 'cancelUpload',
		'click .cancel': 'cancelUpload',
		'click .new-record-options-container li': 'onClickNewRecordOption'
	},

	/** @type {Dataset} */
	model: null,

	/**
	 * #36086 Indicates that a file upload has started. We should not close
	 * the dialog until this flag is reset to false
	 */
	fileUploadInProgress: false,
	/**
	 * #36086 Indicates that the user clicked the blue submit button. If a file
	 * upload is in progress, we will wait until it is complete to perform the
	 * submit; otherwise we will simply submit right away
	 */
	readyToSubmit: false,

	/**
	 * Uploads a new revision of a spreadsheet for a dataset
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {Dataset|Import} options.model
	 *
	 * @param {String} [options.instructions] instructions to display in the
	 * banner
	 */
	initialize: function(options) {
		this.options = options || {}
		this.model = this.options.model
		this.replaceAllRecords = this.model.get('replaceAllRecords')
	},

	render: function() {
		this.$el.empty().append(template())

		this.formView = new FormView({
			el: this.el,
			guard: false,
			// if a file was already specified we're good to go
			allowCleanSubmission: !!this.files,
			submit: this.submit.bind(this)
		})

		this._renderFileDrop()
		this._renderInstructions()
		this._renderNewRecordOptions()
		this._renderWizard()
		this.formView.start()
		this._attachListeners()

		return this
	},

	_renderFileDrop() {
		this.fileDrop = new UploadSpreadsheetFileDrop({
			animated: true,
			fileUploaderOptions: {
				icon: 'docs-document',
				url: this.model.getUploadSpreadsheetUrl(),
				dataFn: () => this.dataFn(),
				accept: ['spreadsheetOrZip'],
				files: this.files,
				alreadyUploaded: !!this.files
			}
		})

		this.$('.import-file').append(this.fileDrop.el)
		this.fileDrop.render()
	},

	_renderInstructions() {
		let instructions = _.get(this.options, 'instructions')
		if (!_.isNil(instructions)) {
			this.$('.banner-description').empty().html(instructions)
		}
	},

	_renderNewRecordOptions() {
		// #34451 only show new record options for datasets
		if (this.model.get('type') === 'dataset') {
			// If dataset has no primary key set
			if (!this.model?.hasPrimaryKeyField?.()) {
				// add the 'no primary key' bullet
				let li = `<li>${i18n.get('datasets.dataSource.spreadsheet.instructions6')}</li>`
				this.$('.banner-description ul').append(li)
			}
			this.newRecordOptions = new NewRecordOptions({
				replaceAllRecords: this.model.get('replaceAllRecords'),
				hasPrimaryKeyField: this.model?.hasPrimaryKeyField?.(),
				noConfirmation: true
			})
			this.$('.new-record-options-container').html(this.newRecordOptions.el)
			this.newRecordOptions.render()
		}
	},

	_renderWizard() {
		if (this.options.wizard) {
			this.options.wizard.render({
				el: this.el,
				formView: this.formView
			})
		}
	},

	_attachListeners() {
		// on 'done', wait an extra moment before calling this.uploadFinished in order to
		// allow time to show the 'done' message inside the datasetsFileUploader
		this.fileDrop.on('done', data => setTimeout(() => this.uploadFinished(data), 1000))
			.on('error', this.uploadError.bind(this))
			.on('start', this.uploadStart.bind(this))
			.on('reset', this.uploadReset.bind(this))
			.on('fileChosen', this.fileChosen.bind(this))
	},

  dataFn: function() {
		let data = { rebuild: !!this.options.rebuild }
		if (this.model.get('type') === 'dataset') {
			data.datasetId = this.model.id
			data.replaceAllRecords = _.isNil(this.replaceAllRecords) ? false :
				this.replaceAllRecords
		} else {
			data.importId = this.model.id
		}
		return data
	},

	fileChosen: function() {
		this.formView.change()
		this.formView.primaryButton.loading()
		// have to use procees.nextTick because formView.change() causes the submit buttons
		// to be updated in process.nextTick, so without it here the button just gets enabled again
		process.nextTick(() => {
			this.formView.primaryButton.disable()
		})
	},

	uploadStart: function(files) {
		this.formView.clearErrors()
		this.fileUploadInProgress = true
	},

	// callback for when the upload finished. data is any data returned by
	// the server
	uploadFinished: function(connection) {
		// #36919 if no data was passed to this function, then this was called
		// by #uploadError and none of this code should run
		if (connection) {
			if (this.model.get('type') === 'dataset') {
				const dataSourceMetadata = _.extend(this.model.get('dataSourceMetadata'), connection)
				this.model.set('dataSourceMetadata', dataSourceMetadata)
				this.formView.change()
				this.fileUploadInProgress = false
				if (this.options.wizard) {
					// #37709 if there are multiple sheets, then we need to show WorksheetOptions
					const hasSheets = connection.sheets && connection.sheets.length > 1
					// #61281 if the dataset was an excel file, and the user uploaded
					// a .csv file that is missing columns, we won't have gotten
					// a full dataset model back from the API, just the exception
					// so we need to detect if the uploaded file is an excel file
					// in this case and show the remapper
					const isExcelFile = _.isNil(dataSourceMetadata?.filename) ?
						this.model.isExcelFile() :
						filenameIsExcelFile(dataSourceMetadata?.filename)
					if (hasSheets && isExcelFile) {
						this.options.wizard.setHasMultipleSheets(true)
						const url = _.result(this.model, 'getSourceDataPreviewUrl')
						$.ajax({
							method: 'post',
							data: JSON.stringify(this.model),
							url,
							success: response => {
								this.model.set(response)
								process.nextTick(() => {
									this.formView.primaryButton.enable()
								})
							},
							error: (xhr) => {
								this.formView.error(xhr)
							}
						})
					}
				}
				// #36919 hide the next step if the user uploads a different file that
				// is valid
				if (this.options.wizard.isLastStep() && this.model.get('type') === 'dataset') {
					this.model.set('replaceAllRecords', this.replaceAllRecords)
					process.nextTick(() => {
						this.formView.primaryButton.enable()
					})
				}
			} else {
				// #38057 if this is an import, close the modal because the connection has been saved
				this.trigger('done', connection)
			}

			if (this.readyToSubmit) {
				this._doSubmit()
			}
		}
	},

	uploadReset: function() {
		delete this.files
		this.formView.change()
		this.fileDrop.reset()
	},

	uploadError: function({ xhr, fileDrop }) {
		if (xhr.statusText !== 'abort') {
			// #29613 When an upload is missing columns present in the dataset, convert
			// this dialog to a wizard so they can map the missing columns to the spreadsheet
			// columns
			this.newColumnChooserOptions = _.extend({}, this.options, {
				replaceAllRecords: this.replaceAllRecords
			})
			if (handleMissingColumnsException(xhr, this.newColumnChooserOptions, false)) {
				// #36919 show the upload as complete and allow moving to the next step
				fileDrop.uploadFinished()
				this.formView.change()
				this.fileUploadInProgress = false
				// update the fileId for when we save the new column names
				const newFileId = xhr?.responseJSON?.fileId
				if (newFileId) {
					const dataSourceMetadata = this.model.get('dataSourceMetadata')
					dataSourceMetadata.fileId = newFileId
					if (!_.isNil(xhr?.responseJSON?.filename)) {
						dataSourceMetadata.filename = xhr?.responseJSON?.filename
					}
					this.model.set('dataSourceMetadata', dataSourceMetadata)
					// #56938 if uploading a new file has caused a missing
					// columns exception, we still want to get a current preview
					// by calling uploadFinished with the updated dataSourceMetadata
					// (which calls the endpoint to get the preview)
					this.uploadFinished(dataSourceMetadata)
				}
				if (this.options.wizard) {
					this.options.wizard.setNewColumnChooserIndexLast()
					// have to use process.nextTick because setNewColumnChooseIndex calls
					// wizard.render() which then results in form-view.js#setupButtons being called
					// which also uses process.nextTick, so the button was always getting re-disabled
					// right after
					process.nextTick(() => {
						this.formView.primaryButton.enable()
					})
				}
			} else {
				fileDrop.reset()
				this.formView.error(xhr)
			}
		}
	},

	onClickNewRecordOption: function(e) {
		this.newRecordOptions.handleOptionSelected(e, replaceAllRecords =>
			// don't save replaceAllRecords to this.model until the user clicks the Import button
			this.replaceAllRecords = replaceAllRecords)
	},

	submit: function() {
		this.newRecordOptions.confirmReplaceIfNeeded((replaceAllRecords, cancelled) => {
			if (cancelled) {
				// #35715 make sure the form properly re-enables if the users clicks
				// cancel on the overwrite confirmation dialog
				this.formView.start()
				this.formView.change()
			} else {
				this.replaceAllRecords = replaceAllRecords
				// If we are still waiting for the file to finish uploading,
				// just flag that the user is ready to submit the form
				if (this.fileUploadInProgress) {
					this.readyToSubmit = true
				} else {
					this._doSubmit()
				}
			}
		})
	},

	/**
	 * Performs the actual import so the dataset can process the new file
	 */
	handleMissingColumns: function(data) {
		this.options.mappingData = data
		this.options.datasetConnection = data.datasetConnection
		this.options.wizard.showNextStep(true)
		this.options.wizard.next(this.newColumnChooserOptions)
	},

	_doSubmit: function() {
		if (this.options.wizard.isLastStep()) {
			const connection = this.model.get('dataSourceMetadata')
			const params = {
				datasetId: this.model.get('id'),
				fileId: connection.fileId,
				replaceAllRecords: this.replaceAllRecords
			}

			if (this.model.get('type') === 'dataset') {
				$.ajax({
					method: 'post',
					url: `${this.model.getImportSpreadsheetUrl()}?${$.param(params)}`,
					success: function (response) {
						this._finish(response)
					}.bind(this)
				})
			} else {
				this._finish(connection)
			}
		} else {
			let options = this.options
			// if there are newColumChooserOptions, that means the next dialog is the new column chooser
			if (!_.isNil(this.newColumnChooserOptions)) {
				options = this.newColumnChooserOptions
			}
			if (!options.replaceAllRecords) {
				options.replaceAllRecords = this.replaceAllRecords
			}
			this.options.wizard.next(options)
		}
	},

	/**
	 * Updates the state of the model and closes the dialog
	 */
	_finish: function(connection) {
		const dataSourceMetadata = _.extend(this.model.get('dataSourceMetadata'), connection)
		this.model.set('dataSourceMetadata', dataSourceMetadata)
		this.model.set('replaceAllRecords', this.replaceAllRecords)

		if (this.options.wizard) {
			if (this.options.wizard.hasMultipleSheets) {
				const nextOpts = _.extend({}, this.options, {
					replaceAllRecords: this.replaceAllRecords
				})
				this.options.wizard.next(nextOpts)
				return
			}
			if (this.options.wizard.isLastStep()) {
				this.options.wizard.finish(this.model)
				this.trigger('done', connection)
				return
			}
		}

		this.trigger('done', connection)
	},

	cancelUpload: function() {
		this.fileDrop.cancel()
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		// #13853 if a spreadsheet is uploading, abort the upload
		this.cancelUpload()
		_.invokeMap([this.formView, this.fileDrop, this.newRecordOptions], 'remove')
	}

})

export default UploadSpreadsheet

UploadSpreadsheet.__filename__ = 'imports/upload-spreadsheet.js'
